import { BUSINESS, NAV_LINKS, SERVICES } from "#/lib/constants";
import { Logo } from "#/components/shared/Logo";
import { Phone, Mail, MapPin, Facebook, Instagram, Star } from "lucide-react";

/**
 * Lets a reader mark us as a preferred source in Google's own settings, which
 * is the only lever a site has on that ranking surface. Square, not the pill
 * the sister site uses, to match this site's rectangular language.
 */
function PreferredSourceCTA() {
  return (
    <a
      href="https://google.com/preferences/source?q=bluerockconcreteco.com"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Set Blue Rock Concrete as a preferred source on Google"
      className="min-h-11 inline-flex items-center gap-2 whitespace-nowrap border border-white/15 bg-white/5 px-3.5 py-2.5 text-xs font-medium text-concrete-light no-underline transition-colors hover:border-flame/60 hover:bg-white/10 hover:text-flame-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame"
    >
      <Star className="h-4 w-4 shrink-0 text-flame" aria-hidden="true" />
      Preferred source on Google
    </a>
  );
}

export function Footer() {
  return (
    <footer className="bg-graphite text-concrete-light">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        {/* Two columns on mobile so Explore and Services sit side by side
            instead of stacking, which is most of the footer's height on a
            phone. Brand and Contact keep the full width. */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-[1.5fr_1fr_1fr_1.2fr] lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-concrete">
              Family-owned concrete contractor serving Delaware, Pennsylvania, and Maryland.
              Driveways, patios, stamped concrete, and more, all built to last through every
              Delaware winter.
            </p>
            <div className="mt-6 flex gap-3">
              <a
                href={BUSINESS.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white/5 text-concrete-light transition-colors hover:bg-flame hover:text-white"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={BUSINESS.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white/5 text-concrete-light transition-colors hover:bg-flame hover:text-white"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
            <div className="mt-5">
              <PreferredSourceCTA />
            </div>
          </div>

          {/* Explore */}
          <div>
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white">Explore</h3>
            <ul className="space-y-0.5">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="flex min-h-11 items-center text-sm text-concrete no-underline transition-colors hover:text-flame-light"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white">Services</h3>
            <ul className="space-y-0.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <a
                    href={`/services#${s.slug}`}
                    className="flex min-h-11 items-center text-sm text-concrete no-underline transition-colors hover:text-flame-light"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white">Get in Touch</h3>
            <ul className="space-y-3.5 text-sm">
              <li>
                <a
                  href={BUSINESS.phoneHref}
                  className="flex items-center gap-3 text-concrete-light no-underline transition-colors hover:text-flame-light"
                >
                  <Phone className="h-4 w-4 shrink-0 text-flame" />
                  {BUSINESS.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="flex items-center gap-3 text-concrete-light no-underline transition-colors hover:text-flame-light"
                >
                  <Mail className="h-4 w-4 shrink-0 text-flame" />
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-concrete">
                <MapPin className="h-4 w-4 shrink-0 text-flame" />
                {BUSINESS.address}
              </li>
            </ul>

            {/* Cross-sell to the sister company. Same family, different trade,
                so this belongs next to the contact details rather than buried
                in the legal line where it used to sit. */}
            <div className="mt-7 border-t border-white/10 pt-6">
              <p className="text-sm leading-relaxed text-concrete">
                Need tree care instead? Our sister company{" "}
                <a
                  href={BUSINESS.sisterUrl}
                  target="_blank"
                  rel="noopener"
                  className="font-semibold text-concrete-light underline decoration-flame/50 underline-offset-2 transition-colors hover:text-flame-light"
                >
                  {BUSINESS.sisterCompany}
                </a>{" "}
                handles trimming, removal, and stump grinding across the same area.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-concrete lg:mt-14 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <p suppressHydrationWarning>
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved. Licensed, bonded &amp; insured.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
            <p>
              Website by{" "}
              <a
                href="https://peakwebtech.com"
                target="_blank"
                rel="noopener"
                className="font-semibold text-concrete-light underline decoration-flame/50 underline-offset-2 transition-colors hover:text-flame-light"
              >
                Peak Web Technologies
              </a>
            </p>
            <nav aria-label="Legal" className="flex items-center gap-6">
              <a
                href="/privacy"
                className="inline-flex min-h-11 items-center text-concrete no-underline transition-colors hover:text-flame-light"
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                className="inline-flex min-h-11 items-center text-concrete no-underline transition-colors hover:text-flame-light"
              >
                Terms of Service
              </a>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
